import { A } from "@solidjs/router";
import { JSX } from "solid-js";
import { downloadHubbleQRCode, HubbleFullSvg } from "~/assets/assets";
import { HubbleLogoTelescope } from "~/components/webstore_telescope/hubble_badge";
import { getCurrentYear } from "~/utils/date";

const GOOGLE_PLAY_BADGE_URL =
  "https://assets-global.website-files.com/637f32081b688877381bdd42/65f7e2f1247b425fc60562e7_Frame%201000001830.png";
const APP_STORE_BADGE_URL =
  "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/white/en-us?size=138x45&releaseDate=1649635200";
const QR_CODE_URL =
  "https://assets.myhubble.money/web-assets/download_hubble_qr_code.png";

export type FooterSectionProps = {
  brandDiscounts: { brandKey: string; discount: number }[];
};

/**
 * Footer Section for Home Webstore Telescope
 * Based on Figma designs: 725-18565 (desktop), 609-29369 (mobile)
 */
export function FooterSection(props: FooterSectionProps): JSX.Element {
  return (
    <footer class="flex w-full justify-center bg-background-normal-primary py-[60px] ">
      <div class="flex w-full max-w-[1024px] flex-col gap-10">
        {/* Logo and Divider */}
        <div class="flex w-full items-center gap-8">
          <div class="flex h-10 w-[169px] shrink-0 items-center">
            {/* <HubbleFullSvg class="h-9" /> */}
            <HubbleLogoTelescope class="!h-9" />
          </div>
          <div class="h-px flex-1 bg-stroke-1" />
        </div>

        {/* Main Content - 3 columns on desktop, stacked on mobile */}
        <div class="flex w-full flex-col gap-8 lg:flex-row lg:items-start">
          {/* Left Column - Contact & Download */}
          <div class="flex flex-1 flex-col gap-8 lg:pr-20">
            <ContactSection />
            <DownloadAppSection />
          </div>

          {/* Middle Column - Categories & Quick Links */}
          <div class="flex w-full flex-col gap-8 lg:w-[260px]">
            <PopularCategoriesSection />
            <QuickLinksSection />
          </div>

          {/* Right Column - Gift Cards & Company */}
          <div class="flex w-full flex-col gap-8 lg:w-[260px]">
            <PopularGiftCardsSection brandDiscounts={props.brandDiscounts} />
            <CompanySection />
          </div>
        </div>

        {/* Footer Bottom - Social & Copyright */}
        <div class="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
          <SocialMediaLinks />
          <div class="hidden h-px flex-1 bg-stroke-1 lg:block" />
          <p class="text-para-3-regular text-text-normal-secondary">
            © {getCurrentYear()} by Gullak Technologies Pvt Ltd
          </p>
        </div>
      </div>
    </footer>
  );
}

function ContactSection(): JSX.Element {
  return (
    <div class="flex flex-col gap-2">
      <h3 class="text-label-semi-bold uppercase text-text-normal-secondary">
        Contact us
      </h3>
      <div class="flex flex-col gap-2 stroke-text-normal-primary">
        <a
          href="mailto:support@myhubble.money"
          class="flex items-start gap-[6px] hover:underline"
        >
          <EmailIcon />
          <span class="text-para-3-regular text-text-normal-primary">
            support@myhubble.money
          </span>
        </a>
        <div class="flex items-start gap-[6px]">
          <LocationIcon />
          <p class="flex-1 text-para-3-regular text-text-normal-primary">
            Urban Vault, #591, 15th main road, 22nd Cross Rd, 3rd Sector, HSR
            Layout, Bengaluru, Karnataka 560102
          </p>
        </div>
      </div>
    </div>
  );
}

function DownloadAppSection(): JSX.Element {
  return (
    <div class="flex flex-col gap-2">
      <h3 class="text-label-semi-bold uppercase text-text-normal-secondary">
        Download app
      </h3>
      <div class="flex items-center gap-3">
        {/* QR Code - Hidden on mobile */}
        <div class="hidden shrink-0 rounded-xl border border-stroke-1 bg-background-normal-primary p-[8px] sm:block">
          <img
            src={downloadHubbleQRCode}
            alt="QR code to download Hubble app"
            class="size-[104px]"
          />
        </div>

        {/* App Store Badges */}
        <div class="flex flex-col gap-3">
          <a
            href="https://play.google.com/store/apps/details?id=money.myhubble&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
            target="_blank"
            rel="noopener noreferrer"
            class="block h-[45px] w-[151px]"
          >
            <img
              src={GOOGLE_PLAY_BADGE_URL}
              alt="Get it on Google Play"
              class="h-full w-full"
            />
          </a>
          <a
            href="https://apps.apple.com/us/app/hubble-shopping-discounts-app/id1614427933"
            target="_blank"
            rel="noopener noreferrer"
            class="block h-[45px] w-[135px]"
          >
            <img
              src={APP_STORE_BADGE_URL}
              alt="Download on the App Store"
              class="h-full w-full "
            />
          </a>
        </div>
      </div>
    </div>
  );
}

function PopularCategoriesSection(): JSX.Element {
  const categories = [
    { label: "Food", slug: "food-gift-cards" },
    { label: "Groceries", slug: "grocery-gift-cards" },
    { label: "Online shopping", slug: "shopping-gift-cards" },
    { label: "Electronics", slug: "electronics-gift-cards" },
    { label: "Fashion", slug: "fashion-gift-cards" },
  ];

  return (
    <div class="flex flex-col gap-2">
      <h3 class="text-label-semi-bold uppercase text-text-normal-secondary">
        Popular categories
      </h3>
      <div class="flex flex-col gap-2">
        {categories.map((category) => (
          <A
            href={`/shop-by-category/${category.slug}`}
            class="text-para-3-medium text-text-normal-primary hover:underline"
          >
            {category.label}
          </A>
        ))}
      </div>
    </div>
  );
}

function QuickLinksSection(): JSX.Element {
  const links = [
    { label: "Privacy policy", href: "/privacy-policy" },
    { label: "Terms & conditions", href: "/terms-and-conditions" },
    { label: "Refund policies", href: "/refund-policy" },
  ];

  return (
    <div class="flex flex-col gap-2">
      <h3 class="text-label-semi-bold uppercase text-text-normal-secondary">
        Quick links
      </h3>
      <div class="flex flex-col gap-2">
        {links.map((link) => (
          <A
            href={link.href}
            target="_blank"
            class="text-para-3-medium text-text-normal-primary hover:underline"
          >
            {link.label}
          </A>
        ))}
      </div>
    </div>
  );
}

type PopularGiftCardsSectionProps = {
  brandDiscounts: { brandKey: string; discount: number }[];
};

function PopularGiftCardsSection(
  props: PopularGiftCardsSectionProps
): JSX.Element {
  const getDiscountForBrand = (brandKey: string, fallback: number): number => {
    return (
      props.brandDiscounts.find((b) => b.brandKey === brandKey)?.discount ??
      fallback
    );
  };

  const giftCards = [
    {
      brandKey: "amazon",
      name: "Amazon",
      fallbackDiscount: 4,
      href: "/buy-gift-card/amazon",
    },
    {
      brandKey: "flipkart",
      name: "Flipkart",
      fallbackDiscount: 1,
      href: "/buy-gift-card/flipkart",
    },
    {
      brandKey: "zomato",
      name: "Zomato",
      fallbackDiscount: 3,
      href: "/buy-gift-card/zomato",
    },
    {
      brandKey: "swiggy",
      name: "Swiggy",
      fallbackDiscount: 3,
      href: "/buy-gift-card/swiggy",
    },
    {
      brandKey: "myntra",
      name: "Myntra",
      fallbackDiscount: 5,
      href: "/buy-gift-card/myntra",
    },
  ];

  return (
    <div class="flex flex-col gap-2">
      <h3 class="text-label-semi-bold uppercase text-text-normal-secondary">
        Popular gift cards
      </h3>
      <div class="flex flex-col gap-2">
        {giftCards.map((card) => {
          const discount = getDiscountForBrand(
            card.brandKey,
            card.fallbackDiscount
          );
          return (
            <A
              href={card.href}
              class="text-para-3-medium text-text-normal-primary hover:underline"
            >
              {card.name} gift card at {discount}% off
            </A>
          );
        })}
      </div>
    </div>
  );
}

function CompanySection(): JSX.Element {
  const links = [
    { label: "About", href: "/about-us" },
    { label: "Blog", href: "/blog" },
    { label: "Careers", href: "/careers" },
    { label: "FAQs", href: "/faqs" },
  ];

  return (
    <div class="flex flex-col gap-2">
      <h3 class="text-label-semi-bold uppercase text-text-normal-secondary">
        Company
      </h3>
      <div class="flex flex-col gap-2">
        {links.map((link) => (
          <A
            href={link.href}
            target="_blank"
            class="text-para-3-medium text-text-normal-primary hover:underline"
          >
            {link.label}
          </A>
        ))}
      </div>
    </div>
  );
}

function SocialMediaLinks(): JSX.Element {
  return (
    <div class="flex gap-[9px]">
      {/* Instagram */}
      <A
        href="https://www.instagram.com/hubblemoney/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram"
        class="flex size-6 items-center justify-center rounded-xl bg-background-inverted-primary stroke-text-inverted-primary"
      >
        <InstagramIcon />
      </A>

      {/* Facebook */}
      <A
        href="https://www.facebook.com/myhubble.money"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Facebook"
        class="flex size-6 items-center justify-center rounded-xl bg-background-inverted-primary stroke-text-inverted-primary"
      >
        <FacebookIcon />
      </A>

      {/* X (Twitter) */}
      <A
        href="https://twitter.com/hubblemoney"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="X"
        class="flex size-6 items-center justify-center rounded-xl bg-background-inverted-primary stroke-text-inverted-primary"
      >
        <XIcon />
      </A>
    </div>
  );
}

// Icon Components
function EmailIcon(): JSX.Element {
  return (
    <svg
      class="size-5 shrink-0"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.3337 5.00004C18.3337 4.08337 17.5837 3.33337 16.667 3.33337H3.33366C2.41699 3.33337 1.66699 4.08337 1.66699 5.00004M18.3337 5.00004V15C18.3337 15.9167 17.5837 16.6667 16.667 16.6667H3.33366C2.41699 16.6667 1.66699 15.9167 1.66699 15V5.00004M18.3337 5.00004L10.0003 10.8334L1.66699 5.00004"
        // stroke="currentColor"
        class="stroke-inherit"
        stroke-width="1.66667"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function LocationIcon(): JSX.Element {
  return (
    <svg
      class="size-5 shrink-0"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 10.8334C11.3807 10.8334 12.5 9.71408 12.5 8.33337C12.5 6.95266 11.3807 5.83337 10 5.83337C8.61929 5.83337 7.5 6.95266 7.5 8.33337C7.5 9.71408 8.61929 10.8334 10 10.8334Z"
        // stroke="currentColor"
        class="stroke-inherit"
        stroke-width="1.66667"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10 18.3334C13.3333 15 16.6667 12.0152 16.6667 8.33337C16.6667 4.65147 13.6819 1.66671 10 1.66671C6.3181 1.66671 3.33333 4.65147 3.33333 8.33337C3.33333 12.0152 6.66667 15 10 18.3334Z"
        // stroke="currentColor"
        class="stroke-inherit"
        stroke-width="1.66667"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function InstagramIcon(): JSX.Element {
  return (
    <svg
      class="size-4"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.3333 1.33337H4.66667C2.82572 1.33337 1.33333 2.82576 1.33333 4.66671V11.3334C1.33333 13.1743 2.82572 14.6667 4.66667 14.6667H11.3333C13.1743 14.6667 14.6667 13.1743 14.6667 11.3334V4.66671C14.6667 2.82576 13.1743 1.33337 11.3333 1.33337Z"
        // stroke="currentColor"
        class="stroke-inherit"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10.6667 7.57999C10.7489 8.13479 10.6542 8.70143 10.3958 9.19873C10.1375 9.69603 9.72877 10.0999 9.22788 10.3521C8.72699 10.6044 8.15881 10.6921 7.60503 10.6033C7.05126 10.5144 6.53947 10.2537 6.14328 9.8575C5.7471 9.46132 5.48636 8.94952 5.39752 8.39575C5.30868 7.84197 5.39634 7.2738 5.64859 6.77291C5.90084 6.27201 6.30471 5.8633 6.80201 5.60496C7.29931 5.34662 7.86595 5.25188 8.42075 5.33412C8.98656 5.41793 9.51026 5.6816 9.91449 6.08583C10.3187 6.49006 10.5824 7.01376 10.6662 7.57957L10.6667 7.57999Z"
        // stroke="currentColor"
        class="stroke-inherit"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <circle cx="11.6667" cy="4.33337" r="0.333333" fill="currentColor" />
    </svg>
  );
}

function FacebookIcon(): JSX.Element {
  return (
    <svg
      class="size-4"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 1.33337H10C9.11594 1.33337 8.26811 1.68456 7.64298 2.30968C7.01786 2.9348 6.66667 3.78265 6.66667 4.66671V6.66671H4.66667V9.33337H6.66667V14.6667H9.33333V9.33337H11.3333L12 6.66671H9.33333V4.66671C9.33333 4.48991 9.40357 4.32033 9.5286 4.19531C9.65362 4.07028 9.8232 4.00004 10 4.00004H12V1.33337Z"
        // stroke="currentColor"
        class="stroke-inherit"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function XIcon(): JSX.Element {
  return (
    <svg
      class="size-4"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.6667 1.33337L5.33333 14.6667M5.33333 1.33337L12.6667 14.6667"
        // stroke="currentColor"
        class="stroke-inherit"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
